import { useCallback } from "react";

import {
  addPx,
  formatTextShadow,
  formatBoxShadow,
  propertiesWithPx,
} from "@/lib/visual-editor/helper";
import { CSSProperty } from "@/types/property-panel.types";

import type { VideoSettings } from "./usePropertyPanelState";

interface UsePropertyPanelHandlersProps {
  onChange?: (property: string, value: any) => void;
  element: any;
  styles: Record<string, string>;
  setStyles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  textShadow: { color: string; x: number; y: number; blur: number };
  setTextShadow: React.Dispatch<
    React.SetStateAction<{
      color: string;
      x: number;
      y: number;
      blur: number;
    }>
  >;
  boxShadow: {
    color: string;
    x: number;
    y: number;
    blur: number;
    spread: number;
  };
  setBoxShadow: React.Dispatch<
    React.SetStateAction<{
      color: string;
      x: number;
      y: number;
      blur: number;
      spread: number;
    }>
  >;
  setContent: React.Dispatch<React.SetStateAction<string>>;
  setLinkUrlButton: React.Dispatch<React.SetStateAction<string>>;
  setImageSrc: React.Dispatch<React.SetStateAction<string>>;
  setClassNames: React.Dispatch<React.SetStateAction<string>>;
  setVisibility: React.Dispatch<
    React.SetStateAction<"none" | "desktop" | "tablet" | "mobile">
  >;
  setLocalOpacity: React.Dispatch<React.SetStateAction<number>>;
  cssProperties: CSSProperty[];
  setCssProperties: React.Dispatch<React.SetStateAction<CSSProperty[]>>;
  setVideoSettings: React.Dispatch<React.SetStateAction<VideoSettings>>;
  setIconName: React.Dispatch<React.SetStateAction<string>>;
  setElementId: React.Dispatch<React.SetStateAction<string>>;
}

export const usePropertyPanelHandlers = ({
  onChange,
  element,
  setStyles,
  textShadow,
  setTextShadow,
  boxShadow,
  setBoxShadow,
  setContent,
  setLinkUrlButton,
  setImageSrc,
  setClassNames,
  setVisibility,
  setLocalOpacity,
  cssProperties,
  setCssProperties,
  setVideoSettings,
  setIconName,
  setElementId,
}: UsePropertyPanelHandlersProps) => {
  // Generic style change handler
  const handleStyleChange = useCallback(
    (property: string, value: string) => {
      console.log("[PropertyPanel] handleStyleChange called:", {
        property,
        value,
      });
      setStyles((prev) => ({ ...prev, [property]: value }));
      onChange?.(property, value);
    },
    [onChange, setStyles],
  );

  // Generic spacing handler
  const handleSpacingChange = useCallback(
    (type: "margin" | "padding", side: string, value: string | number) => {
      const stringValue =
        typeof value === "string" ? value : String(value || "");
      setStyles((prev) => ({
        ...prev,
        [`${type}${side}`]: stringValue,
      }));
      onChange?.(`${type}${side}`, stringValue ? addPx(stringValue) : "");
    },
    [onChange, setStyles],
  );

  // Batch spacing handler: sets multiple sides in one update
  const handleBatchSpacingChange = useCallback(
    (type: "margin" | "padding", sides: Record<string, string | number>) => {
      const updates: Record<string, string> = {};
      for (const [side, value] of Object.entries(sides)) {
        updates[`${type}${side}`] =
          typeof value === "string" ? value : String(value || "");
      }
      setStyles((prev) => ({ ...prev, ...updates }));

      // Call onChange for last side only — it carries all styles via pendingChanges merge
      // Instead, call with special "batchStyles" to set all at once
      const pxUpdates: Record<string, string> = {};
      for (const [key, val] of Object.entries(updates)) {
        pxUpdates[key] = val ? addPx(val) : "";
      }
      onChange?.("batchStyles", pxUpdates);
    },
    [onChange, setStyles],
  );

  // Generic shadow handler
  const handleShadowChange = useCallback(
    (
      type: "text" | "box",
      updatedShadow: typeof textShadow | typeof boxShadow,
    ) => {
      const setter = type === "text" ? setTextShadow : setBoxShadow;
      const formatter = type === "text" ? formatTextShadow : formatBoxShadow;

      setter(updatedShadow as any);
      const shadowString = formatter(updatedShadow as any);
      onChange?.(`${type}Shadow`, shadowString);
      setStyles((prev) => ({
        ...prev,
        [`${type}Shadow`]: shadowString,
      }));
    },
    [onChange, setStyles, setTextShadow, setBoxShadow],
  );

  // Visibility change handler
  // Sets Tailwind responsive classes on the className for the backend save.
  // Note: Preview may not reflect responsive hiding since Tailwind JIT doesn't
  // generate CSS for dynamically-added classes. The effect applies after save.
  const handleVisibilityChange = useCallback(
    (value: "none" | "desktop" | "tablet" | "mobile") => {
      setVisibility(value);

      // Strip existing visibility-related Tailwind classes
      const existingClasses = (element?.attributes?.class || "")
        .split(" ")
        .filter(
          (cls: string) =>
            ![
              "hidden",
              "block",
              "lg:block",
              "md:block",
              "sm:block",
              "lg:hidden",
              "md:hidden",
              "sm:hidden",
            ].includes(cls) && cls.trim() !== "",
        );

      const visibilityClasses: string[] = [];
      if (value === "mobile") {
        visibilityClasses.push("hidden", "sm:block");
      } else if (value === "tablet") {
        visibilityClasses.push("block", "md:hidden", "lg:block");
      } else if (value === "desktop") {
        visibilityClasses.push("block", "lg:hidden");
      }

      const allClasses = [...existingClasses, ...visibilityClasses];
      onChange?.("className", allClasses.join(" "));
    },
    [element?.attributes?.class, onChange, setVisibility],
  );

  // Content and attribute handlers
  const handleContentChange = useCallback(
    (value: string) => {
      setContent(value);
      // Don't push to history if element has bind value (will use "Ask AI to edit" instead)
      if (!element?.bind) {
        onChange?.("content", value);
      }
    },
    [onChange, setContent, element?.bind],
  );

  const handleLinkUrlChange = useCallback(
    (value: string) => {
      setLinkUrlButton(value);
      onChange?.("attributes", {
        ...element?.attributes,
        href: value,
      });
    },
    [element?.attributes, onChange, setLinkUrlButton],
  );

  const handleSrcChange = useCallback(
    (value: string) => {
      setImageSrc(value);
      onChange?.("attributes", {
        ...element?.attributes,
        src: value,
      });
    },
    [element?.attributes, onChange, setImageSrc],
  );

  const handleClassNameChange = useCallback(
    (value: string) => {
      setClassNames(value);
      onChange?.("className", value);
    },
    [onChange, setClassNames],
  );

  // Typography handlers
  const handleLetterSpacingChange = useCallback(
    (value: number | undefined) => {
      setStyles((prev) => ({
        ...prev,
        letterSpacing: value !== undefined ? String(value) : "",
      }));
      onChange?.(
        "letterSpacing",
        value !== undefined ? addPx(String(value)) : "",
      );
    },
    [onChange, setStyles],
  );

  const handleLineHeightChange = useCallback(
    (value: number | undefined) => {
      setStyles((prev) => ({
        ...prev,
        lineHeight: value !== undefined ? String(value) : "",
      }));
      onChange?.("lineHeight", value !== undefined ? String(value) : "");
    },
    [onChange, setStyles],
  );

  // Opacity handlers
  const handleOpacityChange = useCallback(
    (value: number[]) => {
      setLocalOpacity(value[0]);
    },
    [setLocalOpacity],
  );

  const handleOpacityCommit = useCallback(
    (value: number[]) => {
      const opacityValue = (value[0] / 100).toFixed(2);
      handleStyleChange("opacity", opacityValue);
    },
    [handleStyleChange],
  );

  // CSS Properties management
  const addNewCssProperty = useCallback(() => {
    const newProperty: CSSProperty = {
      id: Date.now().toString(),
      property: "color",
      value: "",
    };
    setCssProperties((prev) => [...prev, newProperty]);
  }, [setCssProperties]);

  const removeCssProperty = useCallback(
    (id: string) => {
      setCssProperties((prev) => prev.filter((prop) => prop.id !== id));
    },
    [setCssProperties],
  );

  const updateCssProperty = useCallback(
    (id: string, field: "property" | "value", value: string) => {
      setCssProperties((prev) =>
        prev.map((prop) =>
          prop.id === id ? { ...prop, [field]: value } : prop,
        ),
      );

      const property = cssProperties.find((prop) => prop.id === id);
      if (property && field === "value") {
        let processedValue = value;
        if (propertiesWithPx.includes(property.property)) {
          processedValue = addPx(value);
        }
        onChange?.(property.property, processedValue);
      }
    },
    [cssProperties, onChange, setCssProperties],
  );

  const clearAllCssProperties = useCallback(() => {
    setCssProperties([]);
  }, [setCssProperties]);

  // Video settings handler
  // Sends boolean true/false for toggle attributes, string for text attributes
  const handleVideoSettingChange = useCallback(
    (key: keyof VideoSettings, value: boolean | string) => {
      setVideoSettings((prev) => ({ ...prev, [key]: value }));

      // Map camelCase key to lowercase HTML attribute name
      const attrName = key === "playsInline" ? "playsinline" : key;

      // Only send the single changed attribute — NOT all element attributes.
      // Spreading element?.attributes would overwrite previous pending changes
      // (e.g., toggling autoplay OFF then loop OFF would revert autoplay).
      onChange?.("attributes", {
        [attrName]: value,
      });
    },
    [onChange, setVideoSettings],
  );

  // Generic batch style handler: sets multiple style properties in one atomic update
  const handleBatchStyleChange = useCallback(
    (updates: Record<string, string>) => {
      setStyles((prev) => ({ ...prev, ...updates }));
      onChange?.("batchStyles", updates);
    },
    [onChange, setStyles],
  );

  // Icon name handler
  const handleIconNameChange = useCallback(
    (value: string) => {
      setIconName(value);
      onChange?.("attributes", {
        "data-icon-name": value,
      });
    },
    [onChange, setIconName],
  );

  // Element ID handler (for anchor links)
  const handleElementIdChange = useCallback(
    (value: string) => {
      setElementId(value);
      onChange?.("attributes", {
        ...element?.attributes,
        id: value,
      });
    },
    [onChange, setElementId, element?.attributes],
  );

  return {
    handleStyleChange,
    handleSpacingChange,
    handleBatchSpacingChange,
    handleBatchStyleChange,
    handleShadowChange,
    handleVisibilityChange,
    handleContentChange,
    handleLinkUrlChange,
    handleSrcChange,
    handleClassNameChange,
    handleLetterSpacingChange,
    handleLineHeightChange,
    handleOpacityChange,
    handleOpacityCommit,
    addNewCssProperty,
    removeCssProperty,
    updateCssProperty,
    clearAllCssProperties,
    handleVideoSettingChange,
    handleIconNameChange,
    handleElementIdChange,
  };
};
